import type { PropsWithChildren } from "react";
import { cloneElement, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { AlertCircleIcon } from "@/components/Icons/AlertCircleIcon";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { CheckCircleIcon } from "@/components/Icons/CheckCircleIcon";
import { Toast, ToastViewport } from "@/design-system/Toast";
import { ToastMotionGroup, ToastMotionItem } from "@/ui-adapters/ToastMotion";

export interface FlashToastProps {
  message: string;
  description?: string;
  variant?: "neutral" | "success" | "alert" | "warning";
  duration?: number;
  shouldAutoClose?: boolean;
  "data-testid"?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function FlashToast(props: FlashToastProps): React.ReactNode {
  const getIcon = () => {
    switch (props.variant) {
      case "alert":
        return <AlertCircleIcon />;
      case "success":
        return <CheckCircleIcon />;
      case "warning":
        return <AlertTriangleIcon />;
      case "neutral":
      default:
        return <AlertCircleIcon />;
    }
  };

  return <Toast {...props} icon={cloneElement(getIcon(), { size: 16 })} />;
}

const FlashToastsContext = createContext<{
  show: (props: FlashToastProps) => void;
}>({
  show: () => {},
});

export function useFlashToast(): (props: FlashToastProps) => void {
  const { show } = useContext(FlashToastsContext);

  if (!show) {
    throw new Error("useFlashToast must be used within a FlashToastProvider");
  }

  return show;
}

export function FlashToastProvider({ children }: PropsWithChildren<{}>): React.ReactNode {
  const [current, setCurrent] = useState<Array<FlashToastProps & { id: number }>>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const recentSignatures = useRef(new Map<string, number>());

  const show = useCallback((props: FlashToastProps) => {
    // Drop an identical toast fired within a short window. This collapses the
    // duplicate that StrictMode's double-invoked mount effects produce in dev,
    // and is a harmless no-op in production where effects run once.
    const signature = `${props.variant ?? "neutral"}|${props.message}|${props.description ?? ""}|${props.action?.label ?? ""}`;
    const now = Date.now();
    const lastShownAt = recentSignatures.current.get(signature);
    if (lastShownAt != null && now - lastShownAt < 500) {
      return;
    }
    recentSignatures.current.forEach((shownAt, key) => {
      if (now - shownAt >= 500) {
        recentSignatures.current.delete(key);
      }
    });
    recentSignatures.current.set(signature, now);

    const id = nextId.current;
    nextId.current += 1;

    setCurrent((toasts) => [{ ...props, id }, ...toasts]);

    if (props.shouldAutoClose) {
      const timer = setTimeout(() => {
        setCurrent((toasts) => toasts.filter((toast) => toast.id !== id));
        timers.current.delete(id);
      }, props.duration);

      timers.current.set(id, timer);
    }
  }, []);

  useEffect(() => {
    const activeTimers = timers.current;

    return () => {
      activeTimers.forEach(clearTimeout);
    };
  }, []);

  const flashToastValue = useMemo(
    () => ({
      show: (props: FlashToastProps) =>
        show({
          ...props,
          duration: props.duration ?? 3000,
          shouldAutoClose: props.shouldAutoClose ?? true,
        }),
    }),
    [show],
  );

  return (
    <FlashToastsContext value={flashToastValue}>
      <ToastViewport>
        <ToastMotionGroup>
          {current.map(({ id, ...props }) => (
            <ToastMotionItem key={id}>
              <FlashToast {...props} />
            </ToastMotionItem>
          ))}
        </ToastMotionGroup>
      </ToastViewport>
      {children}
    </FlashToastsContext>
  );
}
